import * as Yup from 'yup';
import { parseBIPDLimits } from 'core/helpers/quoter.service';
import {
  paymentMethod,
  policyType,
  UMPDOnlyWithUMBIStates,
  allow50100UMBIon2550BIStates,
  combinedUMUIM,
  doNotShowCarLevelUMPDStates,
  driverCarAssignmentStates,
  noUDRsAllowedStates,
  policyLevelUMPDStates,
  policyLevelUMPDInUMSelectionStates,
  canRejectPIPStates,
  inexperiencedYearsLimit,
  umpdWithCollisionStates,
  requireDriversLicenseNumberBeforePullingMVRStates,
  isApplicantAnnualMilesEnabled,
  verifiedAnnualMilesMax,
  lookupsJson
} from '@ourbranch/lookups';
import { differenceInYears } from 'date-fns';
import { getTodayStr } from '@ourbranch/date-fns-helpers';

import { capitalize } from 'core/helpers/formatters';
import { driversLicenseNumberSchema } from 'core/helpers/drivers-license-number-validations';
import { FormAction } from 'core/store/offer-store/offer-store';
import { requiredString, validUSState, addDriverNameToError } from 'common/helpers/yup-helpers';
import { getPLimitValue, getUMPDValue, isLimitACPEValid } from 'core/helpers/car-validations';
import { isPrimaryUseNotOfferedByBrand } from 'core/helpers/lookups';
import {
  haveAnyOfThisCarsSymbolsChanged,
  rapaCoverageSymbolSchemaFields,
  vehicleTypeCodes,
  vehicleHorsepowerCodes,
  vehicleSymbolGroups
} from 'common/helpers/car-symbols-helpers';
import { checkZipCodeRange, ZIP_CODE_REGEX } from 'common/helpers/address-helpers';
import {
  companionPolicyShapeFields,
  withPrimaryHomeownerPolicyCheck
} from 'common/validation-schemas/companion-policy.validation-schema';
import { appConfig, brandConfig } from 'brand-config';
import { trusteesSchema } from 'common/components/people/additional-party/add-lien-holders.validation';

Yup.addMethod(Yup.string, 'requiredString', requiredString);
Yup.addMethod(Yup.mixed, 'validUSState', validUSState);
Yup.addMethod(Yup.mixed, 'addDriverNameToError', addDriverNameToError);

// CFM auto collects driver relationship-to-insured and per-vehicle ownership; when a car isn't
// solely owned by the applicant a joint owner is required. Gated on the same brand feature flag the
// UI uses (no brand literals) so render and validation stay in sync. relationshipToInsured mirrors
// the UI's excluded-driver gating too: the field is disabled for excluded drivers, so requiring it
// there would be un-satisfiable.
// Read at schema-build time (a function, not a module const) so tests can flip the brand and the
// guard tracks the active brand — mirrors brandUsesRapaSymbols below. #35809
const collectsVehicleOwnershipDetails = () => appConfig?.featureFlags?.supportsVehicleOwnershipDetails ?? false;
// CFM collects Driver Status (Principal / Occasional) on every rated driver; the CFM rater keys the
// driver class on it, so an unanswered status rated on a fallback (#36327). Same flag driver.jsx shows
// the select on.
const brandRequiresDriverStatus = () => appConfig?.featureFlags?.autoRequiresDriverStatus ?? false;
// Whether Roadside needs a Collision deductible on this brand (@ourbranch/brand-product autoCoverageDependencies, #37618).
// CFM's doesn't, so a liability-only car can carry it. Read at validation time like the guards above; unset keeps
// Branch's rule.
const brandRoadsideRequiresCollision = () => brandConfig?.autoCoverageDependencies?.roadsideRequiresCollision ?? true;
// The ids of @ourbranch/lookups' vehicleOwnershipType, plus null so a nullable schema still accepts
// an unanswered car on the policy side.
const VEHICLE_OWNERSHIP_TYPE_IDS = [...lookupsJson.vehicleOwnershipType.map(({ id }) => id), null];
// CFM "Custom Equipment Coverage": a description is required when the limit (limitACPE) is > 0. #35599
const collectsCustomEquipmentCoverage = appConfig?.featureFlags?.customEquipmentCoverage ?? false;

// CFM (and other farm carriers) rate auto on Verisk/RAPA symbols, so add-car-details.jsx hides the
// Branch make/model/style/aux symbol inputs for them (appConfig.featureFlags.autoUsesRapaSymbols) and
// shows read-only RAPA symbols instead. Validation must honor the same flag — otherwise those hidden,
// always-empty fields fail the "exactly 2 characters" rule and block "Update Offer Price" with issues
// the user can't see or fix (#35517). Read at validate-time so the guard tracks the active brand.
const brandUsesRapaSymbols = () => appConfig?.featureFlags?.autoUsesRapaSymbols ?? false;

// CFM hides the trailer Contents Value field (contents are not a CFM rating input — the rater sends
// trailer_contents 0 when absent), so it can't be required there (#35838). Read at validate-time so
// the guard tracks the active brand.
const brandHidesTrailerContents = () => appConfig?.featureFlags?.hideTrailerContents ?? false;
// CFM collects the trailer make/model (#36346); other brands never carried them, so no constraint there.
const brandRequiresTrailerMakeModel = () => appConfig?.featureFlags?.trailerRequiresMakeModel ?? false;

// A trailer already saved on the offer with no make/model (saved before #36346) is grandfathered —
// matched by VIN against initialValues, like the policy-details schema — so an unrelated update can
// still save. A NEW trailer is not in initialValues, so it stays required; a saved make/model can't
// be cleared.
const trailerMakeModelSchema = (field, label) =>
  brandRequiresTrailerMakeModel()
    ? Yup.string()
        .nullable()
        .test(field, `Trailer ${label} is required`, function (value) {
          if (typeof value === 'string' && value.trim().length > 0) {
            return true;
          }
          const stored = (this.options.context?.initialValues?.trailers ?? []).find(
            (t) => t?.VIN && t.VIN === this.parent?.VIN
          );
          return Boolean(stored) && !stored[field];
        })
    : Yup.string().nullable();
// CFM has no rule tying a trailer's comp/coll deductibles to an insured vehicle's — its filed trailer
// deductibles stop at $1,000 while cars go to $2,500 — so skip the Branch match rule there (#36149).
const brandAllowsIndependentTrailerDeductibles = () =>
  appConfig?.featureFlags?.trailerDeductiblesIndependentOfVehicles ?? false;

// Bill to other (CFM auto, #36200) bills the auto interested party marked Bill to other, mirroring the
// farm interested-party rule: one must exist, with a full address and the email the invoice goes to.
const billToOtherPartySchema = Yup.object().shape({
  name: Yup.string().required('Bill To Other name is required').nullable(),
  email: Yup.string().email('Must be a valid email').required('Bill To Other email is required').nullable(),
  address: Yup.object()
    .shape({
      address: Yup.string().required('Bill To Other address is required').nullable(),
      city: Yup.string().required('Bill To Other city is required').nullable(),
      state: Yup.string().validUSState('address.state').required('Bill To Other state is required').nullable(),
      zip: Yup.string().required('Bill To Other zip is required').nullable()
    })
    .required('Bill To Other address is required')
    .nullable()
});

// #36626: a Trustee's rows each need a trustee name, under any payment method.
const autoLienHolderSchema = ({ billToOther }) =>
  Yup.lazy((party) => {
    if (billToOther && party?.relationship === 'billToOther') {
      return billToOtherPartySchema;
    }
    if (party?.relationship === 'trustee') {
      return Yup.object().shape({ trustees: trusteesSchema }).nullable();
    }
    return Yup.object().nullable();
  });

const autoLienHoldersSchema = Yup.array()
  .when(['$selectedOption', 'global.autoPaymentMethod'], {
    is: (selectedOption, autoPaymentMethod) =>
      selectedOption === policyType.Auto && autoPaymentMethod === paymentMethod.BillToOther,
    then: Yup.array()
      .test(
        'billToOther-exists',
        'A Bill To Other party is required when Bill To Other payment method is selected',
        (lienHolders) => !!lienHolders?.some((party) => party.relationship === 'billToOther')
      )
      .of(autoLienHolderSchema({ billToOther: true }))
      .nullable(),
    otherwise: Yup.array()
      .of(autoLienHolderSchema({ billToOther: false }))
      .nullable()
  })
  .nullable();

const autoSchema = (state, session) => {
  const schema = Yup.object().shape({
    lienHolders: autoLienHoldersSchema,
    autoCoverage: Yup.object().shape({
      policyLimitBIPD: Yup.string()
        .typeError('Choose a bodily injury and property damage limit.')
        .test(
          'umbrellaBIMinimum',
          'Umbrella coverage requires at least $500K per occurrence in auto BI.',
          function (value) {
            if (!this.options.context?.includeUmbrella || !value) {
              return true;
            }
            return (parseBIPDLimits(value)?.limitBI ?? 0) >= 500;
          }
        ),
      policyLimitUMBI: Yup.string()
        .test('umbi', 'UMBI cannot be higher than BIPD', function () {
          const { policyLimitUMBI, policyLimitBIPD } = this.options.context?.autoCoverage || {};
          if (
            !policyLimitUMBI ||
            !policyLimitBIPD ||
            (allow50100UMBIon2550BIStates[this.options.context?.correctedAddress?.state] &&
              policyLimitUMBI === '50/100')
          ) {
            return true;
          }

          const valueUMBI = policyLimitUMBI.includes('/')
            ? Number(policyLimitUMBI.split('/')[0])
            : Number(policyLimitUMBI.split(' ')[0]);
          const valueBIPD = policyLimitBIPD.includes('/')
            ? Number(policyLimitBIPD.split('/')[0])
            : Number(policyLimitBIPD.split(' ')[0]);

          return valueBIPD >= valueUMBI;
        })
        .test('umbiCSL', 'BI cannot be CSL unless policy UM/UIM BI is also CSL.', function () {
          const { policyLimitUMBI, policyLimitBIPD } = this.options.context?.autoCoverage || {};

          const combinedUMUIMState = combinedUMUIM[this.options.context?.correctedAddress?.state];
          if (!combinedUMUIMState || !policyLimitUMBI || !policyLimitBIPD) {
            return true;
          }
          const umbiCSL = policyLimitUMBI.includes('CSL');
          const bipdCSL = policyLimitBIPD.includes('CSL');

          return policyLimitUMBI === '0/0' || umbiCSL === bipdCSL;
        })
        .test('FL_UM/UIMBI_must_match_BIPD', 'UM/UIMBI must match BIPD limits', function (val) {
          const { policyLimitBIPD } = this.options.context?.autoCoverage || {};
          if (this.options.context?.correctedAddress?.state !== 'FL' || !val || !policyLimitBIPD || val === '0/0') {
            return true;
          } // FL UMBI is in this format 10/20 N, but BIPD is in this format 10/20/10
          return policyLimitBIPD.includes(val.split(' ')[0]);
        }),
      policyLimitUIMBI: Yup.string()
        .test('uimbi', 'UIMBI cannot be higher than BIPD', function () {
          const { policyLimitUIMBI, policyLimitBIPD } = this.options.context?.autoCoverage || {};

          if (combinedUMUIM[this.options.context?.correctedAddress?.state] || !policyLimitUIMBI || !policyLimitBIPD) {
            return true;
          }

          const valueUIMBI = policyLimitUIMBI.includes('/')
            ? Number(policyLimitUIMBI.split('/')[0])
            : Number(policyLimitUIMBI.split(' ')[0]);
          const valueBIPD = policyLimitBIPD.includes('/')
            ? Number(policyLimitBIPD.split('/')[0])
            : Number(policyLimitBIPD.split(' ')[0]);
          return valueBIPD >= valueUIMBI;
        })
        .test('uimbiCSL', 'UIM BI cannot be CSL unless policy UM BI and BI/PD are also CSL.', function () {
          const { policyLimitBIPD, policyLimitUIMBI } = this.options.context?.autoCoverage || {};
          const hasAutoPolicy = this.options.context.selectedOption.includes('A');
          const combinedUMUIMState = combinedUMUIM[this.options.context?.correctedAddress?.state];
          if (combinedUMUIMState || !hasAutoPolicy || !policyLimitBIPD || !policyLimitUIMBI) {
            return true;
          }
          const bipdCSL = policyLimitBIPD.includes('CSL');
          const uimbiCSL = policyLimitUIMBI.includes('CSL');
          return policyLimitUIMBI === '0/0' || bipdCSL === uimbiCSL;
        }),
      // Nullable like the policy-side schema: an offer can omit UMPD, and the coverage component
      // already reads a missing value as 'NONE'.
      policyLimitUMPD: Yup.string()
        .nullable()
        .test('UMPDMustBeLowerThanPD', 'UMPD cannot be higher than PD', function () {
          const { policyLimitBIPD, policyLimitUMPD } = this.options.context?.autoCoverage || {};
          const state = this.options.context.correctedAddress?.state;

          if (!policyLimitBIPD || !policyLevelUMPDStates[state]) {
            return true;
          }

          if (state === 'NM') {
            // in NM, we just have a UMPD = PD limit option, dont run validation
            return true;
          }

          const valuePD = policyLimitBIPD.includes('/')
            ? Number(policyLimitBIPD.split('/')[2])
            : Number(policyLimitBIPD.split(' ')[0]);

          if (policyLevelUMPDInUMSelectionStates[state]) {
            // in these states, UMPD is the third value in policyLimitUMBI (e.g. "25/50/25")
            const { policyLimitUMBI } = this.options.context?.autoCoverage || {};
            const umbiParts = policyLimitUMBI?.split('/');
            if (!umbiParts || umbiParts.length < 3) {
              return true;
            }
            const umpdFromUMBI = Number(umbiParts[2]);
            if (Number.isNaN(umpdFromUMBI)) {
              return true; // can't validate, skip
            }
            return valuePD >= umpdFromUMBI;
          }

          const valueUMPD =
            parseFloat(!policyLimitUMPD || policyLimitUMPD === 'NONE' ? '0' : policyLimitUMPD.split('/')[0]) / 1000;

          return valuePD >= valueUMPD;
        })
        .test('UMPDMustHaveUMBIInThiState', 'UMPD cannot be chosen in this state without UMBI', function () {
          const { policyLimitUMBI, policyLimitUMPD } = this.options.context?.autoCoverage || {};
          if (!UMPDOnlyWithUMBIStates[this.options.context?.correctedAddress?.state]) {
            return true;
          }

          if (!policyLimitUMBI || !policyLimitUMPD) {
            return true;
          }

          const valueUMBI = policyLimitUMBI.includes('/')
            ? Number(policyLimitUMBI.split('/')[0])
            : Number(policyLimitUMBI.split(' ')[0]);

          const valueUMPD = Number(policyLimitUMPD === 'NONE' ? '0' : policyLimitUMPD) / 1000;

          return !(valueUMBI === 0 && valueUMPD > 0);
        })
        // No-UMPD states (e.g. MO/CFM) leave this null; without .nullable() Yup rejects it with a
        // "must be a `string` type" error the user can't clear. The tests above already no-op when the
        // value is missing, so nullable only silences the type check (#35517).
        .nullable(),

      policyLimitPIPME: Yup.string()
        .test('requiredForMI', 'PIP medical expense limit is required', function (value) {
          if (this.options.context?.correctedAddress?.state === 'MI') {
            return value?.length;
          }
          return true;
        })
        .nullable(),
      policyLimitPIPACR: Yup.string()
        .test('requiredForMI', 'PIP attendant care rider limit is required', function (value) {
          if (this.options.context?.correctedAddress?.state === 'MI') {
            return value?.length;
          }
          return true;
        })
        .nullable(),
      policyLimitMedicalPayments: Yup.string()
        .test('medicalPaymentsOrComboFBP', 'Medical payments limit is required', function (value) {
          if (this.options.context?.correctedAddress?.state === 'PA') {
            const { policyLimitComboFBP } = this.parent;
            if (policyLimitComboFBP && policyLimitComboFBP !== 'NONE' && value !== 'NONE') {
              return this.createError({
                message: 'Can not choose both this and combined first party benefits'
              });
            }
            if (policyLimitComboFBP && policyLimitComboFBP === 'NONE' && value === 'NONE') {
              return this.createError({
                message: 'Choose medical payments or combined first party benefits'
              });
            }
          }
          return true;
        })
        .test('medicalPaymentsOrPIPNotBothTX', 'Cannot choose both medical payments and PIP in TX', function (value) {
          if (this.options.context?.correctedAddress?.state === 'TX') {
            const { policyLimitPIP } = this.parent;
            if (value !== 'NONE' && policyLimitPIP && policyLimitPIP !== 'NONE') {
              return false;
            }
            return true;
          }
          return true;
        })
        .nullable(),
      policyLimitComboFBP: Yup.string()
        .test('medicalPaymentsOrComboFBP', 'Combined first party benefits limit is required', function (value) {
          if (this.options.context?.correctedAddress?.state === 'PA') {
            const { policyLimitMedicalPayments } = this.parent;
            if (policyLimitMedicalPayments && policyLimitMedicalPayments !== 'NONE' && value !== 'NONE') {
              return this.createError({
                message: 'Can not choose both this and medical payments'
              });
            }
            if (policyLimitMedicalPayments && policyLimitMedicalPayments === 'NONE' && value === 'NONE') {
              return this.createError({
                message: 'Choose medical payments or combined first party benefits'
              });
            }
          }
          return true;
        })
        .nullable(),
      policyLimitPIP: Yup.string()
        .test('medicalPaymentsOrPIPNotBothTX', 'Cannot choose both PIP and medical payments in TX', function (value) {
          if (this.options.context?.correctedAddress?.state === 'TX') {
            const { policyLimitMedicalPayments } = this.parent;
            if (value !== 'NONE' && policyLimitMedicalPayments && policyLimitMedicalPayments !== 'NONE') {
              return false;
            }
            return true;
          }
          return true;
        })
        .test(
          'policyLimitNoFaultPIPIncluded',
          'You must reject PIP coverage in order to opt out of PIP',
          function (value) {
            if (canRejectPIPStates.includes(this.options.context?.correctedAddress?.state)) {
              const { policyLimitNoFaultPIP } = this.parent;
              if (value === 'NONE' && policyLimitNoFaultPIP && policyLimitNoFaultPIP === 'INCLUDED') {
                return false;
              }
              return true;
            }
            return true;
          }
        )
        .test(
          'policyLimitNoFaultPIPNone',
          'PIP coverage must be set to "No Coverage" if PIP is rejected',
          function (value) {
            if (canRejectPIPStates.includes(this.options.context?.correctedAddress?.state)) {
              const { policyLimitNoFaultPIP } = this.parent;
              if (value !== 'NONE' && policyLimitNoFaultPIP && policyLimitNoFaultPIP === 'NONE') {
                return false;
              }
              return true;
            }
            return true;
          }
        )
        .test(
          'numberOfHouseholdMembersMustbeGreaterThan2ToHaveAllApplicants',
          'PIP Deductible must be applied to “Applicant Only” if there is only 1 household resident',
          function (value) {
            if (this.options.context?.correctedAddress?.state === 'FL') {
              const { householdMembers } = this.options.context.auto;
              // NI === Applicant Only / Named Insured
              if (!value.includes('NI') && householdMembers === 1) {
                return false;
              }
              return true;
            }
            return true;
          }
        )
        .nullable()
    }),
    trailers: Yup.array(
      Yup.object().shape({
        VIN: Yup.string().requiredString('VIN is required'),
        type: Yup.string().requiredString('Trailer type is required'),
        make: trailerMakeModelSchema('make', 'make'),
        model: trailerMakeModelSchema('model', 'model'),
        primaryUse: Yup.string().requiredString("Please select the trailer's primary use").nullable(),
        year: Yup.number().typeError('Trailer year should be a number').required('Trailer year is required'),
        value: Yup.number()
          .typeError('Trailer value should be a number between $0 - $50000')
          .positive('Trailer value should be between $0 - $50000')
          .integer('Trailer value should be between $0 - $50000')
          .min(1, 'Trailer value should be greater than $0')
          .max(50000, 'Trailer value should be less than or equal to $50000')
          .when(
            ['deductibleCollision', 'deductibleComprehensive'],
            (deductibleCollision, deductibleComprehensive, schema) => {
              const validValues = [deductibleCollision.split('/')[0], deductibleComprehensive.split('/')[0]].filter(
                (value) => value !== 'NONE' && value
              );

              if (!validValues.length) {
                return schema;
              }
              const minValue = Math.min(...validValues.map((value) => value.split('/')[0]));

              return schema.min(500 + minValue, `Trailer value should be greater than ${500 + minValue}`);
            }
          )
          .required('Trailer value is required'),
        contents: brandHidesTrailerContents()
          ? // No constraints at all for a hiding brand: the user can't see or fix the field, and a
            // stored pre-#35838 value ('' or out-of-range) must not block the save. The CFM rater
            // sends trailer_contents 0 unconditionally, so a stale stored value never rates either.
            Yup.mixed().nullable()
          : Yup.number()
              .typeError('Content value should be a number between $0 - $5000')
              .positive('Content value should be between $0 - $5000')
              .integer('Content value should be between $0 - $5000')
              .min(0, 'Content value should be greater than $0')
              .max(5000, 'Content value should be less than or equal to $5000')
              .required('Trailer content value is required'),

        deductibleCollision: Yup.string()
          .requiredString('Collision deductible is required')
          .test(
            'minimumCollDeductibleMet',
            'Trailer deductibles must match deductibles of at least one insured vehicle, or be NO COVERAGE for liability ONLY',
            (currentTrailerDeductibleCollision, { options }) => {
              if (brandAllowsIndependentTrailerDeductibles()) {
                return true;
              }
              const { context, parent } = options;
              if (currentTrailerDeductibleCollision === 'NONE' && parent.deductibleComprehensive === 'NONE') {
                return true;
              }
              const { cars } = context;
              for (const car of cars) {
                if (
                  car.deductibleCollision === currentTrailerDeductibleCollision &&
                  car.deductibleComprehensive.split('/')[0] === parent.deductibleComprehensive.split('/')[0]
                ) {
                  return true;
                }
              }
              return false;
            }
          ),

        deductibleComprehensive: Yup.string()
          .requiredString('Comprehensive deductible is required')
          .test(
            'minimumCompDeductibleMet',
            'Trailer deductibles must match deductibles of at least one insured vehicle, or be NO COVERAGE for liability ONLY',
            (currentTrailerDeductibleComprehensive, { options }) => {
              if (brandAllowsIndependentTrailerDeductibles()) {
                return true;
              }
              const { context, parent } = options;
              if (currentTrailerDeductibleComprehensive === 'NONE' && parent.deductibleCollision === 'NONE') {
                return true;
              }
              const { cars } = context;
              for (const car of cars) {
                if (
                  car.deductibleComprehensive.split('/')[0] === currentTrailerDeductibleComprehensive.split('/')[0] &&
                  car.deductibleCollision === parent.deductibleCollision
                ) {
                  return true;
                }
              }
              return false;
            }
          ),
        garageLocation: Yup.object().shape({
          address: Yup.string()
            .requiredString('Trailer garage address is required')
            .test('is-po-box', 'Trailer garage address cannot be a PO Box', (value) => {
              const pattern = new RegExp('\\b[p]*(ost)*\\.*\\s*[o|0]*(ffice)*\\.*\\s*b[o|0]x\\b', 'i');
              return !(pattern.test(value) || pattern.test(value));
            }),
          city: Yup.string().requiredString('Trailer garage city is required'),
          state: Yup.string()
            .requiredString('Trailer garage state is required')
            .validUSState('offer.trailers.garageLocation'),
          zip: Yup.string()
            .requiredString('Trailer garage zip code is required')
            .matches(ZIP_CODE_REGEX, { message: 'Zip code must be 5 digits', excludeEmptyString: true })
        })
      })
    ).nullable(),
    drivers: Yup.array()
      .of(
        Yup.object().shape({
          // Without nullable, a null DOB fails here with yup's raw type error instead of reaching the
          // "Date of birth is required" rule in offer.validation-schema.js.
          dateOfBirth: Yup.string()
            .nullable()
            .test('minimum-driver-age', '', function (value) {
              if (value) {
                const today = new Date();
                const birthDate = new Date(value);
                const age = differenceInYears(today, birthDate);
                const { firstName, lastName } = this.parent;

                if (age < 14) {
                  return this.createError({
                    message: `${firstName} ${lastName} is too young to be on the policy`,
                    path: this.path
                  });
                }
              }
              return true;
            }),
          driversLicenseState: Yup.string()
            .validUSState('offer.drivers.driverLicenseState')
            .ensure()
            .addDriverNameToError('Drivers license state'),

          driversLicenseNumber: Yup.string()
            .ensure()
            .test(
              'required-for-certain-states-when-pulling-mvr',
              'Required when pulling MVR',
              function (driversLicenseNumber) {
                const licenseState = this.options.context?.driversLicenseState;
                const offerState = this.options.context?.correctedAddress?.state;
                if (
                  (requireDriversLicenseNumberBeforePullingMVRStates.includes(offerState) ||
                    requireDriversLicenseNumberBeforePullingMVRStates.includes(licenseState)) &&
                  this.options.context.formAction === FormAction.FillSkippedDataPullsAll
                ) {
                  if (!driversLicenseNumber || driversLicenseNumber?.length === 0) {
                    const formattedName = `${capitalize(this.options.parent?.firstName)} ${capitalize(this.options.parent?.lastName)}`;
                    return this.createError({
                      message: `A driver's license number is required for ${formattedName} to pull MVR`
                    });
                  }
                }
                return true;
              }
            )
            .concat(driversLicenseNumberSchema),

          relationshipToInsured: collectsVehicleOwnershipDetails()
            ? Yup.string().when('excludeDriver', {
                is: (excludeDriver) => excludeDriver !== true,
                then: Yup.string().ensure().addDriverNameToError('Relationship to insured'),
                otherwise: Yup.string().nullable()
              })
            : Yup.string().nullable(),
          driverStatus: brandRequiresDriverStatus()
            ? Yup.string().when('excludeDriver', {
                is: (excludeDriver) => excludeDriver !== true,
                then: Yup.string().ensure().addDriverNameToError('Driver status'),
                otherwise: Yup.string().nullable()
              })
            : Yup.string().nullable(),

          schoolName: Yup.string()
            .when('fullTimeStudent', {
              is: (fullTimeStudent) => fullTimeStudent === true,
              then: Yup.string().ensure().addDriverNameToError('School name')
            })
            .nullable(),

          schoolLocation: Yup.object()
            .when('fullTimeStudent', {
              is: (fullTimeStudent) => fullTimeStudent === true,
              then: Yup.object().shape({
                address: Yup.string().ensure().addDriverNameToError('School address'),
                city: Yup.string().ensure().addDriverNameToError('School city'),
                state: Yup.string().ensure().addDriverNameToError('School state'),
                zip: Yup.string().ensure().addDriverNameToError('School zip code').min(5, 'Zip code must be 5 digits')
              })
            })
            .nullable(),

          autoViolations: Yup.object().test(
            'hasUDR',
            "There are drivers on this offer that have an unverified driving record (UDR), and we do not allow that in this state. Please drop the drivers and re-add with Driver's License information to pull driving records.",
            function (obj) {
              if (noUDRsAllowedStates.includes(this.options.context?.correctedAddress?.state)) {
                return !(obj?.UDR >= 1);
              }
              return true;
            }
          ),
          assignedCar: Yup.mixed().test('assignedCar', 'Each driver must be assigned to one car', function (value) {
            if (
              driverCarAssignmentStates[this.options.context?.correctedAddress?.state] &&
              this.options.context?.correctedAddress?.state !== 'MA'
            ) {
              if (!value) {
                return this.createError({
                  message: 'All drivers must be assigned a primary vehicle'
                });
              }
              const cars = this.options.context.cars;
              if (value && !cars.some((car) => car.VIN === value)) {
                return this.createError({
                  message: 'Driver assigned VIN that does not exist '
                });
              }
              const drivers = this.options.context.drivers.filter((driver) => !driver.excludeDriver);
              const assignedDrivers = cars.map((car) => car.assignedDriver);

              if (drivers.length === cars.length) {
                const driversSet = new Set(assignedDrivers);
                // assert that each driver assigned exactly once
                return driversSet.size === assignedDrivers.length && drivers.length === driversSet.size
                  ? true
                  : this.createError({
                      message: 'Each driver must be assigned to one car'
                    });
              }

              return true;
            }
            return true;
          }),
          userAssignedCar: Yup.mixed().test('userAssignedCar', 'This car already has a main driver', function (value) {
            if (
              driverCarAssignmentStates[this.options.context?.correctedAddress?.state] &&
              this.options.context?.correctedAddress?.state === 'MA'
            ) {
              if (!value || value === 'NONE') {
                return true;
              }
              const cars = this.options.context.cars;
              if (value && !cars.some((car) => car.VIN === value)) {
                return this.createError({
                  message: 'Driver assigned VIN that does not exist '
                });
              }
              const inexperiencedDrivers = this.options.context.drivers.filter((driver) => {
                let age = driver.age;
                if (!age) {
                  const today = new Date();
                  const birthDate = new Date(driver.dateOfBirth);
                  age = differenceInYears(today, birthDate);
                }
                return driver.age - driver.ageFirstLicensed < inexperiencedYearsLimit[this.options.context.state];
              });

              if (inexperiencedDrivers.filter((d) => d.userAssignedCar === value).length > 1) {
                return this.createError({
                  message: 'This car already has a main driver'
                });
              }
            }
            return true;
          })
        })
      )
      .nullable(),
    cars: Yup.array()
      .of(
        Yup.object().shape({
          // A car with no VIN can't be rated/bound, but the cars schema never required it — so
          // clearing a VIN on the offer left the form valid and the update went through silently
          // (no validation-errors modal). Require it here so an empty VIN surfaces as a "Vehicles
          // details" error and opens the modal on update/checkout. Mirrors the trailer VIN rule
          // above. (designer PR feedback, Jun 16 — B7a)
          VIN: Yup.string().requiredString('VIN is required'),
          reportedAnnualMiles: isApplicantAnnualMilesEnabled(state, getTodayStr(state))
            ? Yup.number()
                .nullable()
                .integer('Annual miles must be a whole number')
                .moreThan(0, 'Annual miles must be greater than 0')
                .max(
                  verifiedAnnualMilesMax,
                  `Annual miles must be ${verifiedAnnualMilesMax.toLocaleString('en-US')} or less`
                )
            : Yup.number().nullable(),
          // Joint Ownership / PA 1200 (#35809): the owner is identified by EITHER the legacy driver
          // pick or the free-text name — requiring the pick alone blocks non-driver owners, and
          // requiring the name alone blocks every pre-#35809 record. The household/related answers
          // are required on the name path (picking a driver prefills them, so the common path still
          // produces full data); a relationship is required when the owner is related.
          // #36127: "Is the vehicle solely owned by, and registered to, the applicant?" is a Yes/No
          // question a new vehicle starts blank on, so it must be answered; only a No asks for the joint
          // owner below (a blank answer is unanswered, not "not solely owned").
          solelyOwnedByApplicant: collectsVehicleOwnershipDetails()
            ? Yup.boolean()
                .nullable()
                .required('Answer whether the vehicle is solely owned by, and registered to, the applicant')
            : Yup.boolean().nullable(),
          // #36265: new business must say how the vehicle is owned (owned / financed / leased) so
          // underwriting can check the matching interested parties were added.
          ownership: collectsVehicleOwnershipDetails()
            ? Yup.string()
                .nullable()
                .oneOf(VEHICLE_OWNERSHIP_TYPE_IDS, 'Vehicle ownership must be Owned, Financed or Leased')
                .required('Answer how the vehicle is owned')
            : Yup.string().nullable(),
          jointOwner: Yup.string().nullable(),
          jointOwnerName: collectsVehicleOwnershipDetails()
            ? Yup.string()
                .when(['solelyOwnedByApplicant', 'jointOwner'], {
                  is: (solelyOwnedByApplicant, jointOwner) => solelyOwnedByApplicant === false && !jointOwner,
                  then: Yup.string()
                    .ensure()
                    .required('Joint owner name is required when the vehicle is not solely owned by the applicant')
                })
                .nullable()
            : Yup.string().nullable(),
          jointOwnerSameHousehold: collectsVehicleOwnershipDetails()
            ? Yup.boolean()
                .when(['solelyOwnedByApplicant', 'jointOwnerName'], {
                  is: (solelyOwnedByApplicant, jointOwnerName) => solelyOwnedByApplicant === false && !!jointOwnerName,
                  then: Yup.boolean()
                    .typeError('Answer whether the joint owner lives in the same household')
                    .required('Answer whether the joint owner lives in the same household')
                })
                .nullable()
            : Yup.boolean().nullable(),
          jointOwnerRelated: collectsVehicleOwnershipDetails()
            ? Yup.boolean()
                .when(['solelyOwnedByApplicant', 'jointOwnerName'], {
                  is: (solelyOwnedByApplicant, jointOwnerName) => solelyOwnedByApplicant === false && !!jointOwnerName,
                  then: Yup.boolean()
                    .typeError('Answer whether the joint owner is related by blood, marriage or adoption')
                    .required('Answer whether the joint owner is related by blood, marriage or adoption')
                })
                .nullable()
            : Yup.boolean().nullable(),
          jointOwnerRelationship: collectsVehicleOwnershipDetails()
            ? Yup.string()
                .when('jointOwnerRelated', {
                  is: true,
                  then: Yup.string().ensure().required('Relationship is required for a related joint owner')
                })
                .nullable()
            : Yup.string().nullable(),
          // AddressDetailsInput declares address: String!, so a half-filled joint-owner address
          // (city/state/zip without a street) makes AppSync reject the WHOLE save with an opaque
          // required-field error. Catch it here instead (devlo, PR #35863). Inert when untouched.
          jointOwnerAddress: Yup.object()
            .nullable()
            .default(undefined)
            .test(
              'joint-owner-street-required',
              'Joint owner street address is required when any address field is entered',
              (value) => !value || !!value.address || !(value.address2 || value.city || value.state || value.zip)
            ),
          garageLocation: Yup.object().shape({
            // Add Car copies the first car's garage address, so nothing required it at submit and a cleared
            // street/city failed server-side instead (#31976). Mirrors the trailer rule above.
            address: Yup.string().requiredString('Garage address is required'),
            city: Yup.string().requiredString('Garage city is required'),
            state: Yup.string()
              .validUSState('offer.cars.garageLocation.state')
              .test('garage-state-offer-test', function (value) {
                const [_, currentCar] = this.from;
                const { isGarageAddressRuleExceptionApproved } = currentCar.value;
                if (isGarageAddressRuleExceptionApproved) {
                  return true;
                }

                if (value !== this.options?.context.state) {
                  const isInternalOrExternalAgent = session.isInternalSales || session.isExternalSales;
                  return this.createError({
                    message: `Garage address must be in the same state as the offer.${
                      isInternalOrExternalAgent
                        ? ' If an exception needs to be made, please follow your appropriate process for escalating to a Branch underwriting representative.'
                        : ''
                    }`
                  });
                }
                return true;
              }),
            zip: Yup.string()
              .nullable()
              .min(5, 'Zip code must be 5 digits')
              .test('garage-zip-offer-test', function (value) {
                const [_, currentCar] = this.from;
                const { isGarageAddressRuleExceptionApproved } = currentCar.value;
                if (isGarageAddressRuleExceptionApproved) {
                  return true;
                }

                const isValidZipCode = checkZipCodeRange(value, this?.options?.context?.state);
                if (!isValidZipCode) {
                  const isInternalOrExternalAgent = session.isInternalSales || session.isExternalSales;
                  return this.createError({
                    message: `Garage address must be in the valid zip code range of the offer state.${
                      isInternalOrExternalAgent
                        ? ' If an exception needs to be made, please follow your appropriate process for escalating to a Branch underwriting representative.'
                        : ''
                    }`
                  });
                }
                return true;
              })
          }),
          limitUMPD: Yup.mixed()
            .nullable()
            .test(
              'limitUMPDMustBeLowerThanPLimitValue',
              "Error, this can't be higher than property damage limit",
              function (value) {
                const state = this.options.context?.correctedAddress?.state;

                if (state === 'NM' || doNotShowCarLevelUMPDStates[state]) {
                  // in NM, we just have a UMPD = PD limit option and shouldn't run this check.
                  // or we don't have UMPD in this state or car level UMPD in this state
                  // or not selecting auto
                  // so no need to validate
                  return true;
                }
                const { policyLimitBIPD } = this.options.context?.autoCoverage || {};
                if (!policyLimitBIPD) {
                  return true;
                }
                return getUMPDValue(value) <= getPLimitValue(policyLimitBIPD);
              }
            )
            .test(
              'noLimitUMPDWithoutUMBI',
              'Error, UMPD cannot be chosen in this state without UMBI',
              function (value) {
                const state = this.options.context?.correctedAddress.state;
                if (doNotShowCarLevelUMPDStates[state]) {
                  // we don't have UMPD in this state, or car level UMPD in this state, or not selecting Auto so don't run this check
                  return true;
                }
                const { policyLimitUMBI } = this.options.context?.autoCoverage || {};
                if (!policyLimitUMBI) {
                  return true;
                }
                return !(
                  value !== 'NONE' &&
                  UMPDOnlyWithUMBIStates[state] &&
                  (policyLimitUMBI?.startsWith('0/0') || policyLimitUMBI === 'NONE')
                );
              }
            )
            .test('Invalid', 'You may not choose UMPD Coverage when you have Collision coverage', function (value) {
              if (umpdWithCollisionStates.includes(this.options.context.state)) {
                return true;
              }
              if (value && value !== 'NONE') {
                const { deductibleCollision } = this.parent;
                return deductibleCollision === 'NONE';
              }
              return true;
            }),
          deductibleCollision: Yup.string()
            .requiredString('Collision deductible is required')
            .test(
              'minimumCollDeductibleMet',
              'Trailer deductibles must match deductibles of at least one insured vehicle, or be NO COVERAGE for liability ONLY',
              (currentTrailerDeductibleCollision, { options }) => {
                const { context, parent } = options;
                if (currentTrailerDeductibleCollision === 'NONE' && parent.deductibleComprehensive === 'NONE') {
                  return true;
                }
                const { cars } = context;
                for (const car of cars) {
                  if (
                    car.deductibleCollision === currentTrailerDeductibleCollision &&
                    car.deductibleComprehensive?.split('/')[0] === parent.deductibleComprehensive?.split('/')[0]
                  ) {
                    return true;
                  }
                }
                return false;
              }
            )
            .test('Invalid', 'You may not have Collision coverage when you choose UMPD Coverage', function (value) {
              if (umpdWithCollisionStates.includes(this.options.context.state)) {
                return true;
              }
              if (value && value !== 'NONE') {
                const { limitUMPD } = this.parent;
                return !limitUMPD || limitUMPD === 'NONE';
              }
              return true;
            }),
          assignedDriver: Yup.mixed().test(
            'assignedDriver',
            'A driver may only be assigned to one car',
            function (value) {
              if (
                driverCarAssignmentStates[this.options.context?.correctedAddress?.state] &&
                this.options.context?.correctedAddress?.state !== 'MA'
              ) {
                if (!value) {
                  return this.createError({
                    message: 'All cars must be assigned a driver'
                  });
                }
                const cars = this.options.context.cars;
                const drivers = this.options.context.drivers.filter((driver) => !driver.excludeDriver);
                const assignedDrivers = cars.map((car) => car.assignedDriver);
                const driversSet = new Set(assignedDrivers);

                if (drivers.length <= cars.length) {
                  return drivers.length === driversSet.size
                    ? true
                    : this.createError({
                        message: 'Every driver must be assigned to a car'
                      });
                }
                if (drivers.length > cars.length) {
                  return cars.length <= driversSet.size
                    ? true
                    : this.createError({
                        message: 'A driver may only be assigned to one car'
                      });
                }

                return true;
              }
              return true;
            }
          ),
          primaryUse: Yup.string()
            .requiredString('Please select the primary use of the vehicle')
            .nullable()
            // A prefilled use the brand doesn't offer (CV's Commuting default on a CFM car) counts as
            // unanswered: the select can't show it and the brand can't rate it. #37551
            .test(
              'primary-use-offered',
              'Please select the primary use of the vehicle',
              (value, { parent }) => !isPrimaryUseNotOfferedByBrand(value, parent?.garageLocation?.state)
            ),
          customEquipmentDescription: Yup.string()
            .nullable()
            .test(
              'custom-equipment-description-required',
              'Please describe the custom equipment',
              (value, { parent }) => {
                if (!collectsCustomEquipmentCoverage) {
                  return true;
                }
                const acpe = Number(parent.limitACPE);
                if (!Number.isFinite(acpe) || acpe <= 0) {
                  return true;
                }
                return !!value && value.trim().length > 0;
              }
            ),
          // Per-coverage RAPA symbols a UW enters for a farm-carrier auto: two letters or digits each. #36504 #36609
          ...rapaCoverageSymbolSchemaFields(),
          symbolMake: Yup.string()
            .test(
              'symbolMake-length-is-2',
              'Make symbol must be exactly 2 characters',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually) {
                  return true;
                }
                // Hidden for RAPA-symbol brands (CFM) — see brandUsesRapaSymbols above (#35517).
                if (brandUsesRapaSymbols()) {
                  return true;
                }
                if (state === 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length === 2
                  : true;
              }
            )
            .nullable(),
          symbolAux: Yup.string()
            .test(
              'symbolAux-length-is-2',
              'Auxiliary symbol must be exactly 2 characters',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually) {
                  return true;
                }
                // Hidden for RAPA-symbol brands (CFM) — see brandUsesRapaSymbols above (#35517).
                if (brandUsesRapaSymbols()) {
                  return true;
                }
                if (state === 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length === 2
                  : true;
              }
            )
            .nullable(),
          symbolModel: Yup.string()
            .test(
              'symbolModel-length-is-2',
              'Model symbol must be exactly 2 characters',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually) {
                  return true;
                }
                // Hidden for RAPA-symbol brands (CFM) — see brandUsesRapaSymbols above (#35517).
                if (brandUsesRapaSymbols()) {
                  return true;
                }
                if (state === 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length === 2
                  : true;
              }
            )
            .nullable(),
          symbolStyle: Yup.string()
            .test(
              'symbolStyle-length-is-2',
              'Style symbol must be exactly 2 characters',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually) {
                  return true;
                }
                // Hidden for RAPA-symbol brands (CFM) — see brandUsesRapaSymbols above (#35517).
                if (brandUsesRapaSymbols()) {
                  return true;
                }
                if (state === 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length === 2
                  : true;
              }
            )
            .nullable(),
          // Ancestry Review attributes from the versioned symbol table (#35198): optional, but only
          // the filed domains may be stored (the rater substitutes its default for anything else).
          vehicleTypeCode: Yup.string()
            .nullable()
            .test(
              'vehicleTypeCode-allowed',
              `Vehicle type code must be one of ${vehicleTypeCodes.join(', ')}`,
              (value) => !value || vehicleTypeCodes.includes(value.trim().toUpperCase())
            ),
          vehicleHorsepowerCode: Yup.string()
            .nullable()
            .test(
              'vehicleHorsepowerCode-allowed',
              `Horsepower code must be one of ${vehicleHorsepowerCodes.join(', ')}`,
              (value) => !value || vehicleHorsepowerCodes.includes(value.trim().toUpperCase())
            ),
          vehicleSymbolGroup: Yup.string()
            .nullable()
            .test(
              'vehicleSymbolGroup-allowed',
              `Symbol group must be one of ${vehicleSymbolGroups.join(', ')}`,
              (value) => !value || vehicleSymbolGroups.includes(value.trim().toUpperCase())
            ),
          convertible: Yup.boolean().nullable(),
          symbolPGS: Yup.string()
            .test(
              'symbolPGS-length-is-1',
              'PGS symbol must be at least 1 character',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually) {
                  return true;
                }
                // Hidden for RAPA-symbol brands (CFM) — add-car-details.jsx hides the whole non-RAPA
                // branch, FL symbols included, so a future RAPA brand in FL must not validate them (#35517).
                if (brandUsesRapaSymbols()) {
                  return true;
                }
                if (state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 1
                  : true;
              }
            )
            .nullable(),
          symbolBI: Yup.number()
            .test('symbolBI-length-is-required', 'BI symbol is required', (value, { options: { context, parent } }) => {
              if (!context.canAddCarsManually) {
                return true;
              }
              // Hidden for RAPA-symbol brands (CFM) — add-car-details.jsx hides the whole non-RAPA
              // branch, FL symbols included, so a future RAPA brand in FL must not validate them (#35517).
              if (brandUsesRapaSymbols()) {
                return true;
              }
              if (state !== 'FL') {
                return true;
              }
              return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                ? value?.trim().length >= 0
                : true;
            })
            .nullable(),
          symbolPD: Yup.number()
            .test('symbolPD-length-is-required', 'PD symbol is required', (value, { options: { context, parent } }) => {
              if (!context.canAddCarsManually) {
                return true;
              }
              // Hidden for RAPA-symbol brands (CFM) — add-car-details.jsx hides the whole non-RAPA
              // branch, FL symbols included, so a future RAPA brand in FL must not validate them (#35517).
              if (brandUsesRapaSymbols()) {
                return true;
              }
              if (state !== 'FL') {
                return true;
              }
              return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                ? value?.trim().length >= 0
                : true;
            })
            .nullable(),
          symbolMed: Yup.number()
            .test(
              'symbolMed-length-is-required',
              'Med symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually) {
                  return true;
                }
                // Hidden for RAPA-symbol brands (CFM) — add-car-details.jsx hides the whole non-RAPA
                // branch, FL symbols included, so a future RAPA brand in FL must not validate them (#35517).
                if (brandUsesRapaSymbols()) {
                  return true;
                }
                if (state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable(),
          symbolPIP: Yup.number()
            .test(
              'symbolStyle-length-is-required',
              'PIP symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually) {
                  return true;
                }
                // Hidden for RAPA-symbol brands (CFM) — add-car-details.jsx hides the whole non-RAPA
                // branch, FL symbols included, so a future RAPA brand in FL must not validate them (#35517).
                if (brandUsesRapaSymbols()) {
                  return true;
                }
                if (state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable(),
          symbolColl: Yup.number()
            .test(
              'symbolCOLL-length-is-required',
              'Collision symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually) {
                  return true;
                }
                // Hidden for RAPA-symbol brands (CFM) — add-car-details.jsx hides the whole non-RAPA
                // branch, FL symbols included, so a future RAPA brand in FL must not validate them (#35517).
                if (brandUsesRapaSymbols()) {
                  return true;
                }
                if (state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable(),
          symbolComp: Yup.number()
            .test(
              'symbolCOMP-length-is-required',
              'Comprehensive symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually) {
                  return true;
                }
                // Hidden for RAPA-symbol brands (CFM) — add-car-details.jsx hides the whole non-RAPA
                // branch, FL symbols included, so a future RAPA brand in FL must not validate them (#35517).
                if (brandUsesRapaSymbols()) {
                  return true;
                }
                if (state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable(),
          symbolUM: Yup.number()
            .test(
              'symbolUMUIM-length-is-required',
              'UM/UIM symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually) {
                  return true;
                }
                // Hidden for RAPA-symbol brands (CFM) — add-car-details.jsx hides the whole non-RAPA
                // branch, FL symbols included, so a future RAPA brand in FL must not validate them (#35517).
                if (brandUsesRapaSymbols()) {
                  return true;
                }
                if (state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable(),
          limitACPE: Yup.string().test(
            'Invalid',
            'To add Additional Custom Parts Limit, Collision and Comprehensive Deductibles are required',
            function (value) {
              return isLimitACPEValid(value, this.parent);
            }
          ),
          roadsideAssistance: Yup.boolean().test(
            'roadsideRequiresCollision',
            'Roadside Assistance requires a Collision deductible. Please remove Roadside Assistance or add Collision coverage.',
            function (value) {
              if (!value) {
                return true;
              }
              // Branded-title cars (Rebuilt / Lemon buyback) are now treated like any other vehicle
              // (#35176), so the universal rule applies: Roadside requires a Collision deductible.
              if (!brandRoadsideRequiresCollision()) {
                return true;
              }
              const { deductibleCollision } = this.parent;
              return deductibleCollision !== 'NONE';
            }
          )
        })
      )
      .test(
        'garage-state-test',
        'Garage address must be in the same state as the home address for at least one vehicle',
        function (cars) {
          const state = this.options.context?.correctedAddress?.state;
          if (Array.isArray(cars) && cars.length > 0) {
            return cars.some((car) => car.garageLocation?.state === state);
          }
          return true;
        }
      )
      .min(
        1,
        'Please add a car before updating this offer. If there are no cars to insure, please remove auto from the policy type. Branch does not insure auto policies without cars.'
      )
      .nullable(),
    auto: Yup.object().shape({
      householdMembers: Yup.number()
        .test(
          'FL_household_members_must_be_equal_or_greater_than_drivers',
          'The number of household members cannot be lower than the number of active drivers on the policy.',
          function (value) {
            if (this.options.context?.correctedAddress?.state === 'FL') {
              const drivers = this.options.context.drivers.filter(
                (driver) => !(driver?.schoolLocation && driver.schoolLocation.address)
              );

              return !((value || 0) < drivers.length);
            }
            return true;
          }
        )
        .nullable(),
      // CFM auto Underwriting Adjustment (#35787): the ClarionDoor premium_modifier. 0.50–1.50 at
      // 2 decimals for UW entries; empty is fine (the rater applies 1). An UNTOUCHED stored value is
      // grandfathered whatever it is: renewal rate caps write this same field as the cap multiplier
      // (tasks-be renewals auto-rate-cap.ts, which now floors itself at 0.50 to stay inside these
      // bounds — but terms capped before #36285 carry values as low as 0.10), so unconditional
      // bounds would lock every edit on a capped policy behind a field most users can't even see
      // (#35431 lesson; devlo on PR #35852). The decimal check is string-based to dodge float precision
      // (0.95 * 100 !== 95 in JS).
      adjustmentFactor: Yup.number()
        .typeError('Adjustment factor must be a number')
        .test('adjustment-factor-range', 'Adjustment factor must be between 0.50 and 1.50', function (value) {
          if (value === null || value === undefined) {
            return true;
          }
          if (value === this.options.context?.initialValues?.auto?.adjustmentFactor) {
            return true;
          }
          return value >= 0.5 && value <= 1.5;
        })
        .test('two-decimals', 'Adjustment factor allows at most 2 decimal places', function (value) {
          if (value === null || value === undefined) {
            return true;
          }
          if (value === this.options.context?.initialValues?.auto?.adjustmentFactor) {
            return true;
          }
          return /^\d+(\.\d{1,2})?$/.test(String(value));
        })
        .nullable(),
      adjustmentRationale: Yup.string().nullable().max(1000, 'Rationale must be 1,000 characters or fewer'),
      // #35914: Companion Policy Type/Number are required once "Primary Homeowner policy with CFM?" is
      // answered Yes. grandfather:true skips the requirement for an UNCHANGED stored Yes — a migrated
      // GO auto (imported with primaryResidentHomeowner=true but no Companion Type/Number) or an
      // in-force policy isn't blocked retroactively, matching the policy-details schema. A user who
      // NEWLY selects Yes (changed from the stored answer) still must supply them. #35992
      ...companionPolicyShapeFields({ grandfather: true })
    })
  });

  // #35914: block Update Offer Price / Checkout unless the primary-homeowner question is answered Yes.
  return withPrimaryHomeownerPolicyCheck({ grandfather: false })(schema);
};

export default autoSchema;
